// Repair tab body — fourth tab on the market panel. Phase 5 Step 18.
//
// Lists every durability-bearing inventory row whose `durabilityCurrent`
// is below `durabilityMax`. Per row: name (+quality label), durability
// bar with numeric current/max, kit + credit cost, and a "Repair" button
// that's disabled when the player can't afford either side. The disable
// path renders an inline help text identifying which resource is short.
//
// Dumb-renderer pattern matching market-npc-tab.ts: the tab caches the
// last-build snapshot + callbacks so internal state (pending repairs)
// can rebuild without main.ts re-feeding the snapshot. The reducer in
// server-message-reducer.ts calls clearPending(rowid) on the matching
// `itemRepaired` envelope or any `error` carrying a repair-tied code.

import { Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import { parseItemId, QUALITY_COLORS, QUALITY_LABELS } from "../../shared/quality.ts";
import {
  countRepairKits,
  repairableRows,
  repairDisableReason,
  type RepairDisable,
  type RepairTabSnapshot,
} from "./market-repair-helpers.ts";
import {
  BTN_H,
  BUTTON_PALETTE_DEFAULT,
  makeRowButton,
} from "./market-ui-widgets.ts";

const PAD = 12; // contracts:allow-literal — inner padding
const ROW_H = 40; // contracts:allow-literal — taller than listings to fit bar + cost
const ROW_GAP = 4; // contracts:allow-literal
const HEADER_ROW_H = 22; // contracts:allow-literal
const SWATCH_SZ = 18; // contracts:allow-literal
const BTN_W = 84; // contracts:allow-literal — Repair button width
const BAR_W = 120; // contracts:allow-literal — durability bar width
const BAR_H = 8; // contracts:allow-literal — durability bar height
const TABLE_BG = 0x0e0e22;
const TABLE_BORDER = 0x223366;
const ROW_BG = 0x1a1a3a;
const ROW_BG_ALT = 0x151530;
const ROW_BORDER = 0x2a2a55;
const TEXT_BRIGHT = 0xeeeeff;
const TEXT_MUTED = 0x8888aa;
const TEXT_PLACEHOLDER = 0x556688;
const TEXT_WARN = 0xff9988;
const BORDER_DEFAULT = 0x555555;
const BAR_BG = 0x222244;
const BAR_FILL_HEALTHY = 0x55cc77;
const BAR_FILL_LOW = 0xddaa44;
const BAR_FILL_BROKEN = 0xcc4444;
const BODY_SIZE = 11; // contracts:allow-literal
const HELP_SIZE = 10; // contracts:allow-literal

export type { RepairTabSnapshot } from "./market-repair-helpers.ts";

export type RepairTabCallbacks = {
  onRepair: (inventoryRowid: number) => void;
};

export class MarketRepairTab {
  private container: Container | null = null;
  private lastParent: Container | null = null;
  private lastRect: { x: number; y: number; w: number; h: number } | null = null;
  private lastSnap: RepairTabSnapshot | null = null;
  private lastCb: RepairTabCallbacks | null = null;

  // Pending set keyed by rowid — same pattern as MarketNpcTab.buyPending.
  // Cleared by clearPending() called from the itemRepaired reducer.
  private pending = new Set<number>();

  build(
    parent: Container,
    x: number,
    y: number,
    w: number,
    h: number,
    snap: RepairTabSnapshot,
    cb: RepairTabCallbacks,
  ): void {
    this.lastParent = parent;
    this.lastRect = { x, y, w, h };
    this.lastSnap = snap;
    this.lastCb = cb;
    this.render();
  }

  teardown(): void {
    if (this.container) {
      this.container.parent?.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  // Reducer hook — called from server-message-reducer's `itemRepaired`
  // arm and from market-client-handlers' `error` arm when the offending
  // request was a repair. Idempotent.
  clearPending(inventoryRowid: number): void {
    if (!this.pending.has(inventoryRowid)) return;
    this.pending.delete(inventoryRowid);
    this.rebuildIfMounted();
  }

  // __nexusDebug projection — browser tests assert pending lift after
  // the durable confirmation lands.
  get pendingRowids(): number[] { return Array.from(this.pending); }

  private rebuildIfMounted(): void {
    if (this.lastParent && this.lastRect && this.lastSnap && this.lastCb) {
      this.render();
    }
  }

  private render(): void {
    if (!this.lastParent || !this.lastRect || !this.lastSnap || !this.lastCb) return;
    const parent = this.lastParent;
    const { x, y, w, h } = this.lastRect;
    const snap = this.lastSnap;
    const cb = this.lastCb;

    this.teardown();
    const root = new Container();
    root.x = x;
    root.y = y;
    parent.addChild(root);
    this.container = root;

    root.addChild(
      new Graphics().rect(0, 0, w, h).fill({ color: TABLE_BG }).stroke({ color: TABLE_BORDER, width: 1 }), // contracts:allow-literal
    );

    root.addChild(this.buildHeader(w, snap));

    const rows = repairableRows(snap.inventory);
    if (rows.length === 0) {
      const empty = new Text({
        text: "No items need repair.",
        style: { fontSize: BODY_SIZE, fill: TEXT_PLACEHOLDER, fontFamily: "monospace", fontStyle: "italic" },
      });
      empty.x = Math.round((w - empty.width) / 2); // contracts:allow-literal — centering idiom
      empty.y = HEADER_ROW_H + 24; // contracts:allow-literal — gap below header
      root.addChild(empty);
      return;
    }

    const reason = repairDisableReason(snap);
    let rowY = HEADER_ROW_H;
    rows.forEach((slot, idx) => {
      const rowEl = this.buildRow(w, slot, reason, cb, idx % 2 === 1); // contracts:allow-literal — alt shading
      rowEl.y = rowY;
      root.addChild(rowEl);
      rowY += ROW_H + ROW_GAP;
    });
  }

  private buildHeader(w: number, snap: RepairTabSnapshot): Container {
    const header = new Container();
    header.addChild(new Graphics().rect(0, 0, w, HEADER_ROW_H).fill({ color: ROW_BG_ALT }));
    const title = new Text({
      text: "Damaged Equipment",
      style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace", fontWeight: "bold" },
    });
    title.x = PAD;
    title.y = Math.round((HEADER_ROW_H - title.height) / 2); // contracts:allow-literal — centering idiom
    header.addChild(title);

    const kits = countRepairKits(snap.inventory);
    const summary = new Text({
      text: `Repair Kits: ${kits}    \u20B5 ${snap.playerCredits}`,
      style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    summary.x = w - PAD - summary.width;
    summary.y = Math.round((HEADER_ROW_H - summary.height) / 2); // contracts:allow-literal — centering idiom
    header.addChild(summary);
    return header;
  }

  private buildRow(
    w: number,
    slot: InventorySlot,
    resourceReason: RepairDisable,
    cb: RepairTabCallbacks,
    alt: boolean,
  ): Container {
    const row = new Container();
    row.addChild(
      new Graphics()
        .rect(0, 0, w, ROW_H)
        .fill({ color: alt ? ROW_BG_ALT : ROW_BG })
        .stroke({ color: ROW_BORDER, width: 1 }), // contracts:allow-literal
    );

    const cols = columnLayout(w);

    // Item cell — swatch + name (+ quality label if present).
    const itemCell = new Container();
    itemCell.x = cols.item.x + PAD / 2; // contracts:allow-literal — half-pad
    itemCell.y = Math.round((ROW_H - SWATCH_SZ) / 2); // contracts:allow-literal — centering idiom
    row.addChild(itemCell);
    const itemDef = getItemDef(slot.itemId) ?? items.copper;
    const { quality } = parseItemId(slot.itemId);
    const swatchBorder = quality !== null ? QUALITY_COLORS[quality] : BORDER_DEFAULT;
    const swatchBorderW = quality !== null ? 2 : 1; // contracts:allow-literal — quality emphasis
    itemCell.addChild(
      new Graphics()
        .roundRect(0, 0, SWATCH_SZ, SWATCH_SZ, 3) // contracts:allow-literal — corner radius
        .fill({ color: itemDef.color })
        .stroke({ color: swatchBorder, width: swatchBorderW }),
    );
    const labelText = quality !== null ? `${QUALITY_LABELS[quality]} ${itemDef.name}` : itemDef.name;
    const nameText = new Text({
      text: labelText,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    nameText.x = SWATCH_SZ + 6; // contracts:allow-literal — gap
    nameText.y = Math.round((SWATCH_SZ - nameText.height) / 2); // contracts:allow-literal — centering idiom
    itemCell.addChild(nameText);

    // Durability bar + numeric.
    const cur = slot.durabilityCurrent ?? 0;
    const max = slot.durabilityMax ?? 1;
    const barX = cols.durability.x + PAD / 2; // contracts:allow-literal
    const barY = Math.round((ROW_H - BAR_H) / 2); // contracts:allow-literal — centering idiom
    row.addChild(new Graphics().rect(barX, barY, BAR_W, BAR_H).fill({ color: BAR_BG }));
    const ratio = Math.max(0, Math.min(1, cur / max));
    const fillColor = cur === 0 ? BAR_FILL_BROKEN : ratio < 0.4 ? BAR_FILL_LOW : BAR_FILL_HEALTHY; // contracts:allow-literal — visual threshold
    if (ratio > 0) {
      row.addChild(new Graphics().rect(barX, barY, Math.round(BAR_W * ratio), BAR_H).fill({ color: fillColor }));
    }
    const numLbl = new Text({
      text: `${cur}/${max}`,
      style: { fontSize: BODY_SIZE, fill: TEXT_BRIGHT, fontFamily: "monospace" },
    });
    numLbl.x = barX + BAR_W + 6; // contracts:allow-literal — gap
    numLbl.y = Math.round((ROW_H - numLbl.height) / 2); // contracts:allow-literal — centering idiom
    row.addChild(numLbl);

    // Cost cell.
    const matCost = balance.combat.repairMaterialCost;
    const credCost = balance.combat.repairCreditCost;
    const costLbl = new Text({
      text: `${matCost} kit\u00B7 \u20B5 ${credCost}`,
      style: { fontSize: BODY_SIZE, fill: TEXT_MUTED, fontFamily: "monospace" },
    });
    costLbl.x = cols.cost.x + PAD / 2; // contracts:allow-literal — half-pad
    costLbl.y = Math.round((ROW_H - costLbl.height) / 2); // contracts:allow-literal — centering idiom
    row.addChild(costLbl);

    // Action — Repair button + inline help text on disable.
    const rowid = slot.rowid;
    if (rowid === undefined) return row; // guarded by repairableRows; defensive.
    const isPending = this.pending.has(rowid);
    const disabled = isPending || resourceReason !== null;
    const btn = makeRowButton({
      label: "Repair",
      palette: BUTTON_PALETTE_DEFAULT,
      disabled,
      width: BTN_W,
      onClick: () => this.handleRepair(rowid, cb),
      overrideLabel: isPending ? "Repairing\u2026" : undefined,
    });
    btn.x = cols.actions.x + PAD / 2; // contracts:allow-literal
    btn.y = Math.round((ROW_H - BTN_H) / 2); // contracts:allow-literal — centering idiom
    row.addChild(btn);

    if (!isPending && resourceReason !== null) {
      const helpText = resourceReason === "insufficient_kits"
        ? "Need more repair-kits"
        : "Need more credits";
      const help = new Text({
        text: helpText,
        style: { fontSize: HELP_SIZE, fill: TEXT_WARN, fontFamily: "monospace" },
      });
      help.x = cols.actions.x + PAD / 2 + BTN_W + 6; // contracts:allow-literal — gap right of button
      help.y = Math.round((ROW_H - help.height) / 2); // contracts:allow-literal — centering idiom
      row.addChild(help);
    }
    return row;
  }

  private handleRepair(rowid: number, cb: RepairTabCallbacks): void {
    if (this.pending.has(rowid)) return;
    this.pending.add(rowid);
    cb.onRepair(rowid);
    this.rebuildIfMounted();
  }
}

function columnLayout(w: number): {
  item: { x: number; w: number };
  durability: { x: number; w: number };
  cost: { x: number; w: number };
  actions: { x: number; w: number };
} {
  // Flex split: item 28%, durability 32%, cost 16%, actions 24%.
  const itemW = Math.round(w * 0.28); // contracts:allow-literal — column ratio
  const durW = Math.round(w * 0.32); // contracts:allow-literal — column ratio
  const costW = Math.round(w * 0.16); // contracts:allow-literal — column ratio
  const actionsW = w - itemW - durW - costW;
  return {
    item: { x: 0, w: itemW },
    durability: { x: itemW, w: durW },
    cost: { x: itemW + durW, w: costW },
    actions: { x: itemW + durW + costW, w: actionsW },
  };
}
